// src/components/home/ToursSection.jsx

import React from "react";
import {
  Check,
  ArrowRight,
  Plane,
  MapPin,
  Users,
  ShieldCheck,
  Car,
} from "lucide-react";

import Button from "../common/Button";
import banner from "../../assets/vehicles/banner.png";

const items = [
  "Airport Pickup & Drop",
  "Outstation Trips",
  "Local City Travel",
  "Family & Group Travel",
  "Corporate Travel",
  "24/7 Emergency Service",
];

const ToursSection = ({ onBookRide }) => {
  return (
    <section className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div
        className="
          group relative overflow-hidden
          rounded-[2rem]
          bg-[#061A3A]
          shadow-[0_20px_60px_rgba(6,26,58,0.14)]
        "
      >
        {/* =====================================================
            DESKTOP BACKGROUND ANIMATION
            Hidden on mobile
        ====================================================== */}

        <div className="pointer-events-none absolute inset-0 hidden overflow-hidden lg:block">
          {/* Main gradient */}
          <div
            className="
              absolute inset-0
              bg-gradient-to-br
              from-[#020B18]
              via-[#061A3A]
              to-[#005543]
            "
          />

          {/* Large green glow */}
          <div
            className="
              absolute
              -right-40
              -top-40
              h-[600px]
              w-[600px]
              rounded-full
              bg-[#00C878]/15
              blur-[120px]
              animate-pulse
            "
          />

          {/* Lime glow */}
          <div
            className="
              absolute
              -bottom-48
              -left-40
              h-[550px]
              w-[550px]
              rounded-full
              bg-[#8BF108]/10
              blur-[130px]
              animate-pulse
            "
            style={{
              animationDelay: "1.5s",
            }}
          />

          {/* Moving beam */}
          <div
            className="
              njv-light-beam
              absolute
              -left-[30%]
              top-[-20%]
              h-[140%]
              w-[30%]
              rotate-[20deg]
              bg-gradient-to-r
              from-transparent
              via-[#00C878]/15
              to-transparent
              blur-3xl
            "
          />

          {/* Second moving beam */}
          <div
            className="
              njv-light-beam-2
              absolute
              -right-[30%]
              top-[-20%]
              h-[140%]
              w-[25%]
              -rotate-[20deg]
              bg-gradient-to-r
              from-transparent
              via-[#8BF108]/10
              to-transparent
              blur-3xl
            "
          />

          {/* Decorative circles */}
          <div
            className="
              absolute
              left-[8%]
              top-[18%]
              h-2
              w-2
              rounded-full
              bg-[#8BF108]
              shadow-[0_0_25px_7px_rgba(139,241,8,0.25)]
              animate-pulse
            "
          />

          <div
            className="
              absolute
              right-[10%]
              top-[25%]
              h-2
              w-2
              rounded-full
              bg-[#00C878]
              shadow-[0_0_25px_7px_rgba(0,200,120,0.25)]
              animate-pulse
            "
            style={{
              animationDelay: "1s",
            }}
          />

          <div
            className="
              absolute
              bottom-[18%]
              left-[35%]
              h-1.5
              w-1.5
              rounded-full
              bg-white/60
              shadow-[0_0_20px_5px_rgba(255,255,255,0.15)]
              animate-pulse
            "
            style={{
              animationDelay: "2s",
            }}
          />

          {/* Decorative horizontal lines */}
          <div
            className="
              absolute
              left-0
              right-0
              top-1/2
              h-px
              bg-gradient-to-r
              from-transparent
              via-white/5
              to-transparent
            "
          />

          <div
            className="
              absolute
              bottom-12
              left-0
              right-0
              h-px
              bg-gradient-to-r
              from-transparent
              via-[#00C878]/10
              to-transparent
            "
          />
        </div>

        {/* =====================================================
            MAIN CONTENT WRAPPER

            MOBILE:
            Image + Content

            DESKTOP:
            Content only, full width and full height
        ====================================================== */}

        <div className="relative z-10">
          {/* =====================================================
              MOBILE VEHICLE IMAGE
              Hidden on desktop
          ====================================================== */}

          <div
            className="
              relative
              block
              w-full
              overflow-hidden
              bg-white
              lg:hidden
            "
          >
            <div className="relative aspect-[1983/793] w-full">
              <img
                src={banner}
                alt="NJV Tours and Travels vehicles"
                className="
                  absolute
                  inset-0
                  h-full
                  w-full
                  object-contain
                "
                onError={(e) => {
                  e.currentTarget.src = "/images/placeholder.jpg";
                }}
              />

              {/* Bottom fade */}
              <div
                className="
                  pointer-events-none
                  absolute
                  inset-x-0
                  bottom-0
                  h-1/3
                  bg-gradient-to-t
                  from-[#061A3A]/50
                  to-transparent
                "
              />

              {/* Mobile badge */}
              <div
                className="
                  absolute
                  left-3
                  top-3
                  flex
                  items-center
                  gap-1.5
                  rounded-full
                  border
                  border-white/30
                  bg-black/25
                  px-2.5
                  py-1.5
                  text-[9px]
                  font-semibold
                  text-white
                  backdrop-blur-md
                  sm:left-5
                  sm:top-5
                  sm:px-4
                  sm:py-2
                  sm:text-xs
                "
              >
                <span
                  className="
                    h-1.5
                    w-1.5
                    animate-pulse
                    rounded-full
                    bg-[#8BF108]
                    sm:h-2
                    sm:w-2
                  "
                />

                NJV Tours & Travels
              </div>

              {/* Vehicle categories */}
              <div
                className="
                  absolute
                  bottom-3
                  left-3
                  right-3
                  flex
                  flex-wrap
                  gap-1.5
                  sm:bottom-5
                  sm:left-5
                  sm:right-5
                  sm:gap-2
                "
              >
                {["Cars", "Ertiga", "Vans", "Coaches"].map((item) => (
                  <span
                    key={item}
                    className="
                      rounded-full
                      bg-black/35
                      px-2.5
                      py-1
                      text-[9px]
                      font-semibold
                      text-white
                      backdrop-blur-md
                      sm:px-3
                      sm:py-1.5
                      sm:text-[11px]
                    "
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* =====================================================
              CONTENT

              MOBILE:
              Normal section after image

              DESKTOP:
              Full card width + full height
          ====================================================== */}

          <div
            className="
              relative
              flex
              min-h-0
              flex-col
              justify-center
              p-6

              sm:p-8

              lg:min-h-[540px]
              lg:w-full
              lg:p-14
              xl:p-16
            "
          >
            {/* Desktop animated glow */}
            <div
              className="
                pointer-events-none
                absolute
                -right-20
                top-1/2
                hidden
                h-72
                w-72
                -translate-y-1/2
                rounded-full
                bg-[#00C878]/10
                blur-[100px]
                lg:block
              "
            />

            {/* Content inner */}
            <div
              className="
                relative
                z-10
                w-full
                max-w-5xl
                lg:mx-auto
                njv-fade-up
              "
            >
              {/* =================================================
                  EYEBROW
              ================================================== */}

              <div className="flex items-center gap-2">
                <span className="h-px w-8 bg-[#00A260]" />

                <span
                  className="
                    text-[10px]
                    font-bold
                    uppercase
                    tracking-[0.22em]
                    text-[#00C878]
                    sm:text-[11px]
                  "
                >
                  NJV Tours & Travels
                </span>
              </div>

              {/* =================================================
                  HEADING
              ================================================== */}

              <h2
                className="
                  mt-4
                  text-3xl
                  font-extrabold
                  leading-[1.05]
                  tracking-tight
                  text-white

                  sm:text-4xl

                  lg:text-5xl
                  xl:text-6xl
                "
              >
                Travel Comfortable.
                <span className="mt-1 block text-[#00C878]">
                  Reach Happy.
                </span>
              </h2>

              {/* =================================================
                  DESCRIPTION
              ================================================== */}

              <p
                className="
                  mt-5
                  max-w-2xl
                  text-sm
                  leading-6
                  text-white/65

                  sm:text-base
                  sm:leading-7

                  lg:text-[17px]
                  lg:leading-8
                "
              >
                From quick airport transfers to long-distance journeys,
                NJV Tours & Travels provides dependable vehicles and
                professional travel support whenever you need us.
              </p>

              {/* =================================================
                  FEATURE HIGHLIGHTS
              ================================================== */}

              <div
                className="
                  mt-7
                  grid
                  grid-cols-2
                  gap-2.5

                  sm:gap-3

                  lg:grid-cols-4
                  lg:gap-4
                "
              >
                {/* Airport */}
                <div
                  className="
                    group/feature
                    flex
                    items-center
                    gap-2.5
                    rounded-2xl
                    border
                    border-white/10
                    bg-white/[0.05]
                    p-3
                    backdrop-blur-sm
                    transition-all
                    duration-300
                    hover:border-[#00C878]/30
                    hover:bg-white/[0.08]

                    sm:gap-3
                    sm:p-3.5
                  "
                >
                  <div
                    className="
                      flex
                      h-9
                      w-9
                      shrink-0
                      items-center
                      justify-center
                      rounded-xl
                      bg-[#00A260]/15
                      text-[#00C878]
                      transition-transform
                      duration-300
                      group-hover/feature:scale-110

                      sm:h-10
                      sm:w-10
                    "
                  >
                    <Plane className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>

                  <div>
                    <p className="text-[11px] font-bold text-white sm:text-xs">
                      Airport
                    </p>

                    <p className="mt-0.5 text-[9px] text-white/50 sm:text-[10px]">
                      Pickup & Drop
                    </p>
                  </div>
                </div>

                {/* Outstation */}
                <div
                  className="
                    group/feature
                    flex
                    items-center
                    gap-2.5
                    rounded-2xl
                    border
                    border-white/10
                    bg-white/[0.05]
                    p-3
                    backdrop-blur-sm
                    transition-all
                    duration-300
                    hover:border-[#00C878]/30
                    hover:bg-white/[0.08]

                    sm:gap-3
                    sm:p-3.5
                  "
                >
                  <div
                    className="
                      flex
                      h-9
                      w-9
                      shrink-0
                      items-center
                      justify-center
                      rounded-xl
                      bg-[#00A260]/15
                      text-[#00C878]
                      transition-transform
                      duration-300
                      group-hover/feature:scale-110

                      sm:h-10
                      sm:w-10
                    "
                  >
                    <MapPin className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>

                  <div>
                    <p className="text-[11px] font-bold text-white sm:text-xs">
                      Outstation
                    </p>

                    <p className="mt-0.5 text-[9px] text-white/50 sm:text-[10px]">
                      Long distance
                    </p>
                  </div>
                </div>

                {/* Group */}
                <div
                  className="
                    group/feature
                    flex
                    items-center
                    gap-2.5
                    rounded-2xl
                    border
                    border-white/10
                    bg-white/[0.05]
                    p-3
                    backdrop-blur-sm
                    transition-all
                    duration-300
                    hover:border-[#00C878]/30
                    hover:bg-white/[0.08]

                    sm:gap-3
                    sm:p-3.5
                  "
                >
                  <div
                    className="
                      flex
                      h-9
                      w-9
                      shrink-0
                      items-center
                      justify-center
                      rounded-xl
                      bg-[#00A260]/15
                      text-[#00C878]
                      transition-transform
                      duration-300
                      group-hover/feature:scale-110

                      sm:h-10
                      sm:w-10
                    "
                  >
                    <Users className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>

                  <div>
                    <p className="text-[11px] font-bold text-white sm:text-xs">
                      Group Travel
                    </p>

                    <p className="mt-0.5 text-[9px] text-white/50 sm:text-[10px]">
                      Family & Events
                    </p>
                  </div>
                </div>

                {/* Support */}
                <div
                  className="
                    group/feature
                    flex
                    items-center
                    gap-2.5
                    rounded-2xl
                    border
                    border-white/10
                    bg-white/[0.05]
                    p-3
                    backdrop-blur-sm
                    transition-all
                    duration-300
                    hover:border-[#00C878]/30
                    hover:bg-white/[0.08]

                    sm:gap-3
                    sm:p-3.5
                  "
                >
                  <div
                    className="
                      flex
                      h-9
                      w-9
                      shrink-0
                      items-center
                      justify-center
                      rounded-xl
                      bg-[#00A260]/15
                      text-[#00C878]
                      transition-transform
                      duration-300
                      group-hover/feature:scale-110

                      sm:h-10
                      sm:w-10
                    "
                  >
                    <ShieldCheck className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>

                  <div>
                    <p className="text-[11px] font-bold text-white sm:text-xs">
                      24/7 Support
                    </p>

                    <p className="mt-0.5 text-[9px] text-white/50 sm:text-[10px]">
                      Emergency rides
                    </p>
                  </div>
                </div>
              </div>

              {/* =================================================
                  SERVICES
              ================================================== */}

              <div className="mt-7">
                <p
                  className="
                    mb-3
                    text-[10px]
                    font-bold
                    uppercase
                    tracking-[0.18em]
                    text-white/40
                  "
                >
                  What we offer
                </p>

                <div
                  className="
                    grid
                    gap-x-6
                    gap-y-2.5

                    sm:grid-cols-2
                    lg:grid-cols-3
                  "
                >
                  {items.map((item) => (
                    <div
                      key={item}
                      className="
                        flex
                        items-center
                        gap-2
                        text-xs
                        text-white/65
                        transition-colors
                        duration-300
                        hover:text-white
                        sm:text-sm
                      "
                    >
                      <span
                        className="
                          flex
                          h-5
                          w-5
                          shrink-0
                          items-center
                          justify-center
                          rounded-full
                          bg-[#00A260]/15
                        "
                      >
                        <Check className="h-3 w-3 text-[#00C878]" />
                      </span>

                      {item}
                    </div>
                  ))}
                </div>
              </div>

              {/* =================================================
                  CTA
              ================================================== */}

              <div
                className="
                  mt-8
                  flex
                  flex-col
                  gap-4

                  sm:flex-row
                  sm:items-center
                "
              >
                <Button
                  variant="green"
                  size="lg"
                  onClick={onBookRide}
                  className="
                    group/btn
                    w-full
                    shadow-[0_10px_30px_rgba(0,162,96,0.2)]

                    sm:w-auto
                  "
                >
                  Book Your Ride

                  <ArrowRight
                    className="
                      h-4
                      w-4
                      transition-transform
                      duration-300
                      group-hover/btn:translate-x-1
                    "
                  />
                </Button>

                <div
                  className="
                    flex
                    items-center
                    justify-center
                    gap-2
                    text-xs
                    text-white/45

                    sm:justify-start
                  "
                >
                  <span
                    className="
                      h-2
                      w-2
                      animate-pulse
                      rounded-full
                      bg-[#8BF108]
                    "
                  />

                  Available for emergency rides
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ToursSection;